
import Vue from 'vue';
import type { PropOptions } from 'vue';
import { validationMixin } from 'vuelidate';
import { generateForm, getVisibleFields } from './utils/common';
import BravadoSelectV3 from '@/components/common/BravadoSelectV3/BravadoSelectV3.vue';
import BravadoTextInputV3 from '@/components/common/BravadoTextInputV3/BravadoTextInputV3.vue';
import {
  REQUIRED_ERROR,
  TYPE_NUMBERS_ONLY_ERROR,
  BETWEEN_0_AND_100_ERROR,
  OTE_IS_LOWER_THAN_BASE_SALARY_ERROR,
  GREATER_THAN_ZERO_ERROR,
} from '@/components/partials/jobs/constants/validationErrors';
import JobsCompaniesSelect from '@/components/partials/jobs/JobsCompaniesSelect/JobsCompaniesSelect.vue';
import JobsMultiselectWithCustomOption from '@/components/partials/jobs/JobsMultiselectWithCustomOption.vue';
import type {
  GenerateFormPayload,
  Form,
  JobsFormConfig,
  FormValidations,
  JobsFormFieldWithRenderedAttrs,
  FormFieldValidationRules,
} from '@/components/partials/public-portfolio/PortfolioJobsInquirySection/PortfolioJobsInquirySectionProfile/types';
import JobsSelectWithCustomOption from '@/components/partials/jobs/JobsSelectWithCustomOption.vue';
import PortfolioAutofocusFormFieldMixin from '@/components/partials/portfolio/PortfolioAutofocusFormFieldMixin';
import type { Company, JobsInquiry, CurrentUser } from '@/types/graphql';

interface IData {
  attemptedSubmission: boolean;
  form: Form;
}

export default Vue.extend({
  name: 'JobsInquiryForm',
  components: {
    BravadoTextInputV3,
    BravadoSelectV3,
    JobsCompaniesSelect,
    JobsSelectWithCustomOption,
    JobsMultiselectWithCustomOption,
  },
  mixins: [validationMixin, PortfolioAutofocusFormFieldMixin],
  props: {
    config: {
      type: Object,
      required: true,
    } as PropOptions<JobsFormConfig>,
    inquiry: {
      type: Object,
      required: true,
    } as PropOptions<JobsInquiry>,
    user: {
      type: Object,
      default: () => ({} as CurrentUser),
    } as PropOptions<CurrentUser>,
    adminPrivilege: {
      type: Boolean,
      default: false,
    } as PropOptions<boolean>,
    fullCompanyName: {
      type: Boolean,
      default: false,
    } as PropOptions<boolean>,
    disabled: {
      type: Boolean,
      default: false,
    } as PropOptions<boolean>,
  },
  validations(): { form: FormValidations } {
    return {
      form: {
        ...(this.config?.getValidation &&
          this.config.getValidation({
            form: this.form,
            hasAdminPrivilege: this.adminPrivilege,
          })),
      },
    };
  },
  data(): IData {
    return {
      attemptedSubmission: false,
      // @ts-ignore
      form: this.generateForm({ fields: this.config.fields, inquiry: this.inquiry, user: this.user }),
    };
  },
  computed: {
    fields(): JobsFormFieldWithRenderedAttrs[] {
      return getVisibleFields({ fields: this.config?.fields, form: this.form });
    },
    errors(): Record<string, string | undefined> {
      if (!this.attemptedSubmission) return {};

      return (this.config?.fields ?? []).reduce((acc: Record<string, string | undefined>, { attributeName }) => {
        const field: FormFieldValidationRules = this.$v.form[attributeName];
        if (!field) return acc;
        if (!field.$invalid) return acc;

        let errorMessage;

        if (field.numeric === false) {
          errorMessage = TYPE_NUMBERS_ONLY_ERROR;
        } else if (field.between === false) {
          errorMessage = BETWEEN_0_AND_100_ERROR;
        } else if (field.oteIsLowerThanBaseSalary === false) {
          errorMessage = OTE_IS_LOWER_THAN_BASE_SALARY_ERROR;
        } else if (field.greaterThanZero === false) {
          errorMessage = GREATER_THAN_ZERO_ERROR;
        } else {
          errorMessage = REQUIRED_ERROR;
        }

        acc[attributeName] = errorMessage;

        return acc;
      }, {});
    },
  },
  watch: {
    inquiry(newInquiry): void {
      this.form = this.generateForm({ fields: this.config.fields, inquiry: newInquiry, user: this.user });
    },
  },
  methods: {
    handleSave(): void {
      this.attemptedSubmission = true;
      this.$v.form.$touch();

      if (this.$v.form.$invalid) {
        this.$emit('error');
        return;
      }

      const { inquiryAttrs, userAttrs } = this.config.prepareFormForUpsert({
        fields: this.config.fields,
        form: this.form,
      });

      this.$emit('upsert', {
        inquiryAttrs,
        userAttrs,
      });
    },
    handleChangeSelectWithCustomOption(payload: Record<string, string | null>): void {
      this.form = { ...this.form, ...payload };
    },
    handleChangeClients({ companies }: { companies: Company[] }): void {
      this.form.solidClients = companies.map(({ id }) => id);
    },
    generateForm(formPayload: GenerateFormPayload): Form {
      const handler = this.config.generateForm ?? generateForm;
      return handler(formPayload);
    },
  },
});
